import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Modal, Form, Upload, Icon, message,
} from 'antd';
import { axiosContext } from '../../../api/axios';

const { Dragger } = Upload;
const IcrAttachmentModal = Form.create()(({
  visible, constituentId, infoCaptureRequestCheckId, icrId, handleCancel, activePanelTypeName, groupData, isPreviewMode,
}) => {
  const [uploaded, setUploaded] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [fileList, setFileList] = useState([]);
  const moreThanOneFile = useRef(false);

  const getLatestAttachmentDetails = useCallback(async () => {
    // In preview mode, return empty array without API call
    if (isPreviewMode) {
      return [];
    }

    try {
      const axiosInstance = axiosContext(localStorage.icrToken);
      const response = await axiosInstance({
        method: 'get',
        url: `/api/info_capture_request/${icrId}/attachments`,
      });
      const { groupCode } = groupData;
      const filteredData = response.data.filter(item => item.screeningGroup.code === groupCode);
      return filteredData;
    } catch (error) {
      return [];
    }
  }, [icrId, groupData, isPreviewMode]);

  useEffect(() => {
    const getCurrentInfo = async () => {
      const res = await getLatestAttachmentDetails();
      setUploadedCount(res.length);
    };

    if (visible) {
      setFileList([]);
      getCurrentInfo();
    }

    if (!visible) {
      setUploaded(false);
      setUploadStatus(false);
      setUploadedCount(0);
    }
  }, [getLatestAttachmentDetails, visible]);

  const handleOk = ev => {
    if (!uploaded) {
      message.error('Please upload an attachment!');
      return;
    }
    handleCancel();
  };

  const uploadAttachment = async (file, constituentId, infoCaptureRequestCheckId) => {
    try {
      const axiosInstance = axiosContext(localStorage.icrToken);
      const response = await axiosInstance({
        method: 'post',
        url: `/api/info_capture_request/${icrId}/attachments`,
        data: { info_capture_request_check_id: infoCaptureRequestCheckId, constituent_id: constituentId, filename: file.name, type: file.type },
      });

      if (response.data._errors) return response.data._errors;
      const formData = new FormData();
      Object.entries(response.data.upload.fields).forEach(
        ([key, value]) => formData.append(key, value),
      );
      formData.append('Content-Disposition', `attachment; filename="${file.name}"`);
      formData.append('Content-Type', file.type);
      formData.append('file', file);

      const opts = {
        method: 'POST',
        body: formData,
        auth: false,
        headers: { accept: 'application/json' },
      };

      const res = await fetch(response.data.upload.url, opts);
      return res.status;
    } catch (error) {
      throw new Error(error.message);
    }
  };


  const updateUploadStatus = async (latestAttachments = []) => {
    try {
      const { groupInfo } = groupData;
      const currentAttachmentCount = latestAttachments.length;
      setUploadedCount(currentAttachmentCount);
      const { minAttachmentCount = 0, maxAttachmentCount } = groupInfo;
      if (minAttachmentCount > 0 && currentAttachmentCount < minAttachmentCount) return false;
      const effectiveMax = maxAttachmentCount ?? 5;
      return currentAttachmentCount >= effectiveMax;
    } catch (error) {
      return true;
    }
  };

  const customRequest = ({ file, onError, onProgress, onSuccess }) => {
    onProgress();
    uploadAttachment(file, constituentId, infoCaptureRequestCheckId).then(
      async action => {
        if (action) {
          message.success('Successfully uploaded attachment!');
          onSuccess({});
          setUploaded(true);
          const response = await getLatestAttachmentDetails();
          const status = await updateUploadStatus(response);
          setUploadStatus(status);
        } else {
          message.error("We're having trouble uploading your file right now.");
          onError(action.response, action.response.body);
          if (this.props.onSaveFailed) this.props.onSaveFailed(action);
        }
      },
    ).catch(err => {
      message.error('Failed to submit attachment');
      onError();
    });
  };

  const getUploadInfo = () => {
    const { groupInfo } = groupData;
    if (!groupInfo) return;

    const { maxAttachmentCount } = groupInfo;
    const effectiveMax = maxAttachmentCount ?? 5;


    // eslint-disable-next-line consistent-return
    return (
      <div style={{ marginBottom: '25px' }}>
        <div>Upload status: <strong style={{ color: '#C88242' }}> {uploadedCount}/{effectiveMax} </strong></div>
      </div>
    );
  };

  return (
    <Modal
      title={<spa>Add Evidence for <strong style={{ color: '#82412C' }}>{activePanelTypeName}</strong></spa>}
      visible={visible}
      onCancel={handleCancel}
      destroyOnClose={true}
      onOk={handleOk}
      okButtonProps={{ disabled: isPreviewMode }}
      style={{ minWidth: 800 }}
    >
      <Form layout="vertical">
        {isPreviewMode && (
          <div style={{
            marginBottom: '16px', padding: '12px', backgroundColor: '#fffbe6', border: '1px solid #ffe58f', borderRadius: '4px', color: '#8c8c8c',
          }}
          >
            <strong>Preview Mode:</strong> Attachment upload is disabled in preview mode.
          </div>
        )}
        {getUploadInfo()}
        <Form.Item>
          <Dragger
            style={{ padding: '16px' }}
            accept="application/pdf,image/jpeg,image/gif,image/png,image/tiff,image/bmp,image/heic,image/heif"
            multiple={false}
            customRequest={customRequest}
            disabled={uploadStatus || isPreviewMode}
            beforeUpload={(_, fileList) => {
              if (fileList.length > 1) {
                message.error('Only one file can be uploaded at a time');
                moreThanOneFile.current = false;
                return false;
              }
              moreThanOneFile.current = true;
              return true;
            }}
            onChange={({ _, fileList: newFileList }) => {
              if (!moreThanOneFile.current) {
                return;
              }

              setFileList(newFileList);
            }}
            fileList={fileList}

          >
            <p className="ant-upload-drag-icon">
              <Icon type="inbox" />
            </p>
            <p className="ant-upload-text">Click or drag file to this area to upload</p>
          </Dragger>
        </Form.Item>
      </Form>
    </Modal>
  );
});

export { IcrAttachmentModal };
